<!--
 * @Description: 月度销售额统计 - ECharts 版本
 * @Author: xiawenlong
 * @Date: 2024-12-13 10:49:11
 * @LastEditors: xiawenlong
 * @LastEditTime: 2025-11-28 09:18:18
-->
<template>
  <div class="salesAmount">
    <div class="title">
      <img src="@/img/datav/title_icon.png" alt="" />
      <span>月度销售额统计(万元)</span>
    </div>
    <div ref="chartContainer" class="chart-container"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { annualSaleAmount } from '@/api/dashboard'
import to from 'await-to'

let myChart = null
let highlightTimer = null

export default {
  data() {
    return {
      monthList: [],
      amountList: [],
    }
  },
  mounted() {
    this.initChart()
    this.initData()

    setInterval(() => {
      this.initData()
    }, process.env.VUE_APP_LTIME)

    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (highlightTimer) {
      clearInterval(highlightTimer)
    }
    if (myChart) {
      myChart.dispose()
      myChart = null
    }
  },
  methods: {
    initChart() {
      myChart = echarts.init(this.$refs.chartContainer)
    },
    handleResize() {
      if (myChart) {
        myChart.resize()
      }
    },
    async initData() {
      const [res, err] = await to(annualSaleAmount({ type: 3 }))
      if (err) return

      this.monthList = res.data.monthList
      this.amountList = res.data.amountList
      this.updateChart()
    },
    updateChart() {
      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(2, 14, 40, 0.9)',
          borderColor: '#16CEB9',
          borderWidth: 1,
          textStyle: {
            color: '#fff',
            fontSize: 14,
          },
          formatter: function(params) {
            const data = params[0]
            return `<div style="font-size:14px;font-weight:bold;margin-bottom:5px;">${data.name}</div>
                    <div style="display:flex;align-items:center;">
                      <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${data.color};margin-right:8px;"></span>
                      <span>销售额: </span>
                      <span style="font-weight:bold;color:#00E0DB;margin-left:5px;">${data.value} 万元</span>
                    </div>`
          },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '12%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: this.monthList,
          boundaryGap: false,
          axisLine: {
            lineStyle: {
              color: 'rgba(22, 206, 185, 0.3)',
            },
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: 14,
            interval: 0,
            rotate: 0,
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: 'rgba(22, 206, 185, 0.1)',
              type: 'dashed',
            },
          },
        },
        yAxis: {
          type: 'value',
          min: 0,
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: 14,
            formatter: '{value}',
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(22, 206, 185, 0.1)',
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: '销售额',
            type: 'line',
            data: this.amountList,
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            showSymbol: true,
            lineStyle: {
              width: 3,
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 1,
                y2: 0,
                colorStops: [
                  { offset: 0, color: '#00E0DB' },
                  { offset: 0.5, color: '#16CEB9' },
                  { offset: 1, color: '#4BD782' },
                ],
              },
              shadowColor: 'rgba(22, 206, 185, 0.5)',
              shadowBlur: 10,
              shadowOffsetY: 5,
            },
            itemStyle: {
              color: '#020E28',
              borderColor: '#16CEB9',
              borderWidth: 2,
              shadowColor: 'rgba(22, 206, 185, 0.8)',
              shadowBlur: 8,
            },
            emphasis: {
              scale: 1.5,
              itemStyle: {
                color: '#16CEB9',
                borderColor: '#fff',
                borderWidth: 2,
                shadowColor: 'rgba(22, 206, 185, 1)',
                shadowBlur: 15,
              },
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(22, 206, 185, 0.4)' },
                  { offset: 0.5, color: 'rgba(22, 206, 185, 0.15)' },
                  { offset: 1, color: 'rgba(22, 206, 185, 0.01)' },
                ],
              },
            },
            label: {
              show: true,
              position: 'top',
              distance: 10,
              color: '#00E0DB',
              fontSize: 13,
              fontWeight: 'bold',
              formatter: '{c}',
              backgroundColor: 'rgba(2, 14, 40, 0.6)',
              padding: [4, 8],
              borderRadius: 4,
              borderColor: 'rgba(22, 206, 185, 0.3)',
              borderWidth: 1,
            },
            animationDuration: 2000,
            animationEasing: 'cubicOut',
            animationDelay: function(idx) {
              return idx * 100
            },
          },
        ],
      }

      myChart.setOption(option)

      this.addChartEffects()
    },
    addChartEffects() {
      let currentIndex = -1
      const dataLen = this.amountList.length

      const autoHighlight = () => {
        myChart.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
        })

        currentIndex = (currentIndex + 1) % dataLen

        myChart.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: currentIndex,
        })

        myChart.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: currentIndex,
        })
      }

      if (highlightTimer) {
        clearInterval(highlightTimer)
      }
      highlightTimer = setInterval(autoHighlight, 3000)

      myChart.on('mouseover', function() {
        clearInterval(highlightTimer)
        myChart.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
        })
      })

      myChart.on('mouseout', function() {
        highlightTimer = setInterval(autoHighlight, 3000)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.salesAmount {
  position: relative;
  height: 100%;
  box-sizing: border-box;
  padding: 20px 30px;

  .title {
    width: 550px;
    position: relative;
    font-size: 26px;
    color: #ecf8ff;
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    padding-top: 10px;

    img {
      margin-right: 8px;
    }
  }

  .chart-container {
    width: 100%;
    height: calc(100% - 80px);
    min-height: 350px;
  }
}
</style>
